import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Chip,
  TablePagination,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { Close, Search, Warning } from '@mui/icons-material';
import { useLowStockItems } from '../hooks/useDashboardData';

interface LowStockModalProps {
  open: boolean;
  onClose: () => void;
}

export const LowStockModal: React.FC<LowStockModalProps> = ({ open, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const { data, isLoading } = useLowStockItems(page + 1, rowsPerPage);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter by search term on client side
  const filteredItems = data?.items?.filter((item) =>
    searchTerm === '' ||
    item.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.barcode.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.symbol.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="warning" />
            <Typography variant="h5" fontWeight={700}>
              Low Stock Items
            </Typography>
            <Chip label={data?.totalCount || 0} color="warning" size="small" />
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          placeholder="Search by name, barcode, or symbol..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Item Name</strong></TableCell>
                    <TableCell><strong>Barcode</strong></TableCell>
                    <TableCell><strong>Symbol</strong></TableCell>
                    <TableCell align="right"><strong>On Hand</strong></TableCell>
                    <TableCell align="right"><strong>Min</strong></TableCell>
                    <TableCell align="right"><strong>Max</strong></TableCell>
                    <TableCell align="right"><strong>Variance</strong></TableCell>
                    <TableCell><strong>Warehouse</strong></TableCell>
                    <TableCell><strong>Properties</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredItems.map((item) => (
                    <TableRow key={item.unitId} hover>
                      <TableCell>{item.itemName}</TableCell>
                      <TableCell>{item.barcode}</TableCell>
                      <TableCell>{item.symbol}</TableCell>
                      <TableCell align="right">
                        <Typography color="warning.main" fontWeight={600}>
                          {item.onHandQuantity}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{item.minNumber}</TableCell>
                      <TableCell align="right">{item.maxNumber}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={item.variance}
                          size="small"
                          color="error"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{item.warehouseName}</Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {item.warehouseCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {item.additionalProperties && (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {Object.entries(item.additionalProperties).map(([key, value]) => (
                              <Chip
                                key={key}
                                label={`${key}: ${value}`}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={data?.totalCount || 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
